'use client'

import { api, type UserProfile } from '@/lib/api'
import { hasAuthSession } from '@/lib/auth-client'

export interface AwarenessUserState {
  name: string
  color: string
  colorLight: string
  initials: string
  username?: string
  avatarUrl?: string
}

function profileInitials(firstName: string, lastName: string): string {
  const first = firstName.trim().charAt(0)
  const last = lastName.trim().charAt(0)
  const combined = `${first}${last}`.toUpperCase()
  return combined || '?'
}

function profileDisplayName(profile: UserProfile): string {
  const parts = [profile.first_name, profile.middle_name, profile.last_name]
    .map((p) => p?.trim())
    .filter(Boolean)
  if (parts.length > 0) return parts.join(' ')
  if (profile.username?.trim()) return profile.username.trim()
  return profile.email.split('@')[0] || 'User'
}

function profileAvatarUrl(profile: UserProfile & Record<string, unknown>): string | undefined {
  const candidates = [
    profile.avatar_url,
    profile.profile_url,
    profile.profile_image_url,
    profile.photo_url,
    profile.image_url,
  ]
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return undefined
}

function colorWithLightVariant(color: string): string {
  const hslMatch = color.match(/^hsl\((\d+)\s+([\d.]+)%\s+([\d.]+)%\)$/)
  if (hslMatch) {
    return `hsla(${hslMatch[1]}, ${hslMatch[2]}%, ${hslMatch[3]}%, 0.2)`
  }
  if (/^#[0-9a-fA-F]{6}$/.test(color)) {
    return `${color}33`
  }
  return 'rgba(59, 130, 246, 0.2)'
}

export function stableAwarenessColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  const hue = h % 360
  return `hsl(${hue} 65% 48%)`
}

export function buildAwarenessUserFromProfile(profile: UserProfile): AwarenessUserState {
  const name = profileDisplayName(profile)
  const color = stableAwarenessColor(profile.id || profile.email || name)
  return {
    name,
    color,
    colorLight: colorWithLightVariant(color),
    initials: profileInitials(profile.first_name, profile.last_name),
    username: profile.username?.trim() || undefined,
    avatarUrl: profileAvatarUrl(profile as UserProfile & Record<string, unknown>),
  }
}

export function buildAwarenessUserFallback(label: string, clientId: number): AwarenessUserState {
  const name = label.trim() || `Guest-${clientId}`
  const color = stableAwarenessColor(name)
  return {
    name,
    color,
    colorLight: colorWithLightVariant(color),
    initials: name.slice(0, 2).toUpperCase(),
  }
}

let cachedProfile: AwarenessUserState | null = null
let profilePromise: Promise<AwarenessUserState | null> | null = null

export async function fetchAwarenessUserFromProfile(): Promise<AwarenessUserState | null> {
  if (!hasAuthSession()) return null
  if (cachedProfile) return cachedProfile
  if (profilePromise) return profilePromise

  profilePromise = (async () => {
    try {
      const profile = await api.getProfile()
      const user = buildAwarenessUserFromProfile(profile)
      cachedProfile = user
      return user
    } catch {
      return null
    } finally {
      profilePromise = null
    }
  })()

  return profilePromise
}

export function clearAwarenessUserProfileCache(): void {
  cachedProfile = null
  profilePromise = null
}
